import { Block } from '../classes/Block';
import type { CardGroup } from './cardGroups';

export interface CardTimingValues {
  cardFadeInFrames: number;
  cardDurationFullyUpFrames: number;
  cardFadeOutFrames: number;
  cardSlugFrames: number;
}

export interface CardTimelineItem extends CardTimingValues {
  cardGroupId: string;
  cardName: string;
  blockNames: string[];
  cardDisplayName: string;
  color: string;
  blockIds: string[];
  startFrame: number;
  endFrame: number;
  durationFrames: number;
  isMixedTiming: boolean;
  isLocked: boolean;
}

export interface AutoFitTargetResult {
  cardCount: number;
  fixedPerCardFrames: number;
  availableFullyUpFrames: number;
  uniformFullyUpFrames: number;
  actualTotalFrames: number;
  deltaFrames: number;
}

const FALLBACK_COLOR: string = '#5b8dd9';

const toNonNegativeInt = (value: number): number => {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.floor(value));
};

export const getCardTotalFrames = (timing: CardTimingValues): number => {
  return (
    toNonNegativeInt(timing.cardFadeInFrames) +
    toNonNegativeInt(timing.cardDurationFullyUpFrames) +
    toNonNegativeInt(timing.cardFadeOutFrames) +
    toNonNegativeInt(timing.cardSlugFrames)
  );
};

export const getBlockTimingValues = (block: Block): CardTimingValues => ({
  cardFadeInFrames: toNonNegativeInt(block.cardFadeInFrames ?? 0),
  cardDurationFullyUpFrames: toNonNegativeInt(block.cardDurationFullyUpFrames ?? 0),
  cardFadeOutFrames: toNonNegativeInt(block.cardFadeOutFrames ?? 0),
  cardSlugFrames: toNonNegativeInt(block.cardSlugFrames ?? 0),
});

export const formatFramesToClock = (totalFrames: number, fps: number): string => {
  const safeFps: number = toNonNegativeInt(fps);
  const safeFrames: number = toNonNegativeInt(totalFrames);

  if (safeFps <= 0) {
    return `00:00:${String(safeFrames).padStart(2, '0')}`;
  }

  const wholeSeconds: number = Math.floor(safeFrames / safeFps);
  const frameRemainder: number = safeFrames % safeFps;
  const minutes: number = Math.floor(wholeSeconds / 60);
  const seconds: number = wholeSeconds % 60;
  const frameWidth: number = Math.max(2, String(safeFps - 1).length);

  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}:${String(frameRemainder).padStart(frameWidth, '0')}`;
};

export const parseClockToFrames = (input: string, fps: number): number | null => {
  const trimmed: string = input.trim();
  if (!trimmed) return null;
  const safeFps: number = toNonNegativeInt(fps);
  if (safeFps <= 0) return null;

  const parts: string[] = trimmed.split(':').map((part: string) => part.trim());

  if (parts.length === 1) {
    const framesOnly: number = Number(parts[0]);
    return Number.isFinite(framesOnly) ? toNonNegativeInt(framesOnly) : null;
  }

  if (parts.length === 2) {
    const minutes: number = Number(parts[0]);
    const seconds: number = Number(parts[1]);
    if (!Number.isFinite(minutes) || !Number.isFinite(seconds)) return null;
    return toNonNegativeInt(minutes) * 60 * safeFps + toNonNegativeInt(seconds) * safeFps;
  }

  if (parts.length === 3) {
    const minutes: number = Number(parts[0]);
    const seconds: number = Number(parts[1]);
    const frames: number = Number(parts[2]);
    if (!Number.isFinite(minutes) || !Number.isFinite(seconds) || !Number.isFinite(frames)) return null;
    return (
      toNonNegativeInt(minutes) * 60 * safeFps +
      toNonNegativeInt(seconds) * safeFps +
      toNonNegativeInt(frames)
    );
  }

  return null;
};

export const buildCardTimeline = (
  blocks: Block[],
  cardGroups: CardGroup[]
): CardTimelineItem[] => {
  const blockById: Map<string, Block> = new Map<string, Block>(blocks.map((block: Block) => [block.id, block]));

  const normalizedGroups: Array<{ id: string; name: string; color: string; blockIds: string[] }> =
    cardGroups.length > 0
      ? cardGroups.map((group: CardGroup) => ({
          id: group.id,
          name: group.name,
          color: group.color,
          blockIds: group.blockIds,
        }))
      : blocks.map((block: Block, index: number) => ({
          id: block.id,
          name: `Card ${index + 1}`,
          color: FALLBACK_COLOR,
          blockIds: [block.id],
        }));

  let currentStartFrame: number = 0;
  const items: CardTimelineItem[] = [];

  normalizedGroups.forEach((group, index: number) => {
    const groupBlocks: Block[] = group.blockIds
      .map((blockId: string) => blockById.get(blockId))
      .filter((block: Block | undefined): block is Block => block !== undefined);

    if (groupBlocks.length === 0) {
      return;
    }

    const firstTiming: CardTimingValues = getBlockTimingValues(groupBlocks[0]);
    const isMixedTiming: boolean = groupBlocks.some((block: Block) => {
      const timing: CardTimingValues = getBlockTimingValues(block);
      return (
        timing.cardFadeInFrames !== firstTiming.cardFadeInFrames ||
        timing.cardDurationFullyUpFrames !== firstTiming.cardDurationFullyUpFrames ||
        timing.cardFadeOutFrames !== firstTiming.cardFadeOutFrames ||
        timing.cardSlugFrames !== firstTiming.cardSlugFrames
      );
    });

    const durationFrames: number = getCardTotalFrames(firstTiming);
    const isLocked: boolean = groupBlocks.some((block: Block) => block.cardTimingLocked);
    const startFrame: number = currentStartFrame;
    const endFrame: number = startFrame + durationFrames;
    const blockNames: string[] = groupBlocks
      .map((block: Block) => block.name?.trim())
      .filter((name: string | undefined): name is string => Boolean(name));
    const cardName: string = group.name || `Card ${index + 1}`;
    currentStartFrame = endFrame;

    items.push({
      cardGroupId: group.id,
      cardName,
      blockNames,
      cardDisplayName: blockNames.length > 0 ? `${cardName} (${blockNames.join(', ')})` : cardName,
      color: group.color || FALLBACK_COLOR,
      blockIds: groupBlocks.map((block: Block) => block.id),
      startFrame,
      endFrame,
      durationFrames,
      isMixedTiming,
      isLocked,
      ...firstTiming,
    });
  });

  return items;
};

export const calculateAutoFitTarget = (
  cardCount: number,
  targetFrames: number,
  fadeInFrames: number,
  fadeOutFrames: number,
  slugFrames: number,
  preservedFrames: number = 0
): AutoFitTargetResult => {
  const safeCardCount: number = Math.max(0, Math.floor(cardCount));
  const safeTargetFrames: number = toNonNegativeInt(targetFrames);
  const safePreservedFrames: number = toNonNegativeInt(preservedFrames);
  const fixedPerCardFrames: number =
    toNonNegativeInt(fadeInFrames) + toNonNegativeInt(fadeOutFrames) + toNonNegativeInt(slugFrames);
  const fixedAllCardsFrames: number = safeCardCount * fixedPerCardFrames;
  const availableFullyUpFrames: number = Math.max(0, safeTargetFrames - safePreservedFrames - fixedAllCardsFrames);
  const uniformFullyUpFrames: number =
    safeCardCount > 0 ? Math.max(0, Math.round(availableFullyUpFrames / safeCardCount)) : 0;
  const actualTotalFrames: number = safePreservedFrames + safeCardCount * (fixedPerCardFrames + uniformFullyUpFrames);

  return {
    cardCount: safeCardCount,
    fixedPerCardFrames,
    availableFullyUpFrames,
    uniformFullyUpFrames,
    actualTotalFrames,
    deltaFrames: actualTotalFrames - safeTargetFrames,
  };
};
